import { Controller } from "@hotwired/stimulus"

// Tasteful idle marquee for the trust-row. The track only animates when the
// section is in view AND the user has been idle (no scroll) for >= 3 seconds.
// Touching the scroll or hovering the track pauses it again. Reduced-motion
// users get a fully static list.
//
// Markup contract:
//   <div data-controller="idle-marquee">
//     <div data-idle-marquee-target="track">
//       (... children to scroll horizontally ...)
//     </div>
//   </div>
//
// The controller automatically duplicates the track's children once so the
// CSS `marquee` keyframes can loop seamlessly.

const IDLE_DELAY_MS = 3000

export default class extends Controller {
  static targets = ["track"]
  static values  = { speedSeconds: { type: Number, default: 60 } }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (!this.hasTrackTarget) return

    this._duplicate()
    this._track = this.trackTarget
    this._track.classList.add("idle-marquee__track")
    this._track.style.animationDuration = `${this.speedSecondsValue}s`

    this._inView = false
    this._idle = false
    this._hovering = false
    this._idleTimer = null

    this._observer = new IntersectionObserver(([entry]) => {
      this._inView = entry.isIntersecting
      this._reschedule()
    }, { threshold: 0.2 })
    this._observer.observe(this.element)

    this._onScroll = () => { this._idle = false; this._sync(); this._reschedule() }
    window.addEventListener("scroll", this._onScroll, { passive: true })
    if (window.__lenis) {
      this._lenisOnScroll = () => this._onScroll()
      window.__lenis.on("scroll", this._lenisOnScroll)
    }

    this._onEnter = () => { this._hovering = true;  this._sync() }
    this._onLeave = () => { this._hovering = false; this._sync() }
    this._track.addEventListener("mouseenter", this._onEnter)
    this._track.addEventListener("mouseleave", this._onLeave)

    // Initialise idle clock so animation kicks in 3 s after page paints.
    this._reschedule()
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    window.removeEventListener("scroll", this._onScroll)
    if (window.__lenis && this._lenisOnScroll) window.__lenis.off("scroll", this._lenisOnScroll)
    if (this._track) {
      this._track.removeEventListener("mouseenter", this._onEnter)
      this._track.removeEventListener("mouseleave", this._onLeave)
    }
    if (this._idleTimer) clearTimeout(this._idleTimer)
  }

  _duplicate() {
    // Clone children once so the marquee CSS can shift by -50% and loop.
    const t = this.trackTarget
    if (t.dataset.duplicated) return
    const clone = t.cloneNode(true)
    while (clone.firstChild) t.appendChild(clone.firstChild)
    t.dataset.duplicated = "true"
  }

  _reschedule() {
    if (this._idleTimer) clearTimeout(this._idleTimer)
    this._idleTimer = setTimeout(() => {
      this._idle = true
      this._sync()
    }, IDLE_DELAY_MS)
  }

  _sync() {
    if (!this._track) return
    const shouldRun = this._inView && this._idle && !this._hovering
    this._track.classList.toggle("idle-marquee__track--running", shouldRun)
  }
}
